import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Loader2, AlertCircle, InfoIcon } from 'lucide-react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/redux/store';
import { useLocation } from 'react-router-dom';
import { setIsValid } from '@/redux/slices/network/netWorkSlice';
import { showErrorToast, showWarningToast } from '@/components/toast/toast';
import oktaService from '@/services/OktaService';
import { backendApiUrl } from '@/lib/config';
interface EditBannerProps {
  stage: string;
  component: string;
  palletId: number;
  onEditStateChange: (isEditing: boolean) => void;
}
const EditingMessage = ({isEditing, currentEditor}: {isEditing: boolean, currentEditor: string | null}) => {
  let message: string;

if (isEditing) {
  message = "You are currently editing this component";
} else if (currentEditor) {
  message = `This component is being edited by ${currentEditor}`;
} else {
  message = "This component is in read-only mode. Click 'Enable Editing' to make changes.";
}

return (<span className="text-sm lg:text[16px] leading-[150%] tracking-[0.15px]">
  {message}
</span> )   
}
const EditBanner: React.FC<EditBannerProps> = ({ stage, component, palletId, onEditStateChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditor, setCurrentEditor] = useState<string | null>(null);
  const { projectId } = useSelector((state: RootState) => state.define);
  const isEditingRef = useRef(false);
  console.log(isEditing, 'isEditing');
  const pathname = useLocation()
  const dispatch = useDispatch()
  const { isValid } = useSelector((state: RootState) => state.netWork);
  const [isLoading, setIsLoading] = useState(false)
  const releaseEditAccess = useCallback(async (updateUi = true) => {
    try {
      // setIsLoading(true)
      const oktaUser = await oktaService.getUser();
      if (oktaUser.access_token) {
        await axios.post(`${backendApiUrl}/edit/release-access`, {
          stage,
          component,
          projectId,
          palletId
        }, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${oktaUser.access_token}`,
            // issite: 'true'
          }
        });

        isEditingRef.current = false;
        if (updateUi) {
          setIsEditing(false);
          onEditStateChange(false);
        }
      }
    } catch (error) {
      console.error('Error releasing edit access:', error);
    } finally {
      // setIsLoading(false)
    }
  }, [stage, component, projectId, palletId, onEditStateChange]);

  const validateConfig = async (): Promise<boolean> => {

    try {
      if (pathname.pathname.includes('offline')) {
        // Use offline validation
        // const result = await ValidationService.validatePalletConfig(Number(projectId!));
        // setIsValid(result.isValid);
        // if (!result.isValid) {
        //   showErrorToast('Please fill all necessary fields of previous stage before moving forward');
        // }
        dispatch(setIsValid(true))
        return true
      } else {
        // Use online validation
        // if(!isValid) {
        console.log('validating config:', projectId)
        const oktaUser = await oktaService.getUser();
        if (oktaUser.access_token) {
          const response = await axios.get(
            `${backendApiUrl}/projects/validatePalletConfig/${projectId}`,
            {
              withCredentials: true,
              headers: {
                Authorization: `Bearer ${oktaUser.access_token}`,
                // issite: 'true'
              }
            }
          );
          console.log('response:', response)
          const data = response.data;
          dispatch(setIsValid(data.isValid));
          if (!data.isValid) {
            showErrorToast('Please fill all necessary fields of previous stage to enable editing');
          }
          return data.isValid;
          // }else{
          //   return true
          // }
        }

      }
    } catch (error: any) {
      console.error('Error validating config:', error);
      if (error?.response?.status === 404) {
        showErrorToast('Please fill all necessary fields of previous stage before moving forward');
        return false;
      }
      showErrorToast('Error validating configuration');
      return false;
    }
  };

  useEffect(() => () => {
    if (isEditingRef.current) {
      void releaseEditAccess(false);
    }
  }, [releaseEditAccess]);

  const requestEditAccess = async () => {
    try {
      // setIsLoading(true)
      const checkValid = await validateConfig()
      console.log(checkValid, 'checkValid')
      if (!checkValid) {
        return
      }
      const oktaUser = await oktaService.getUser();
      // console.log(oktaUser.access_token,'oktaUser.access_token')
      if (oktaUser.access_token) {
        console.log('requesting access')
        const response = await axios.post(`${backendApiUrl}/edit/request-access`, {
          stage,
          component,
          projectId,
          palletId
        }
          , {
            withCredentials: true,
            headers: {
              "authorization": `Bearer ${oktaUser.access_token}`,
              // issite: 'true'
            }
          }
        );
        console.log('response:', response)
        isEditingRef.current = true;
        setIsEditing(true);
        onEditStateChange(true);
      }
    } catch (error: any) {
      if (error.response?.data?.editor) {
        setCurrentEditor(error.response.data.editor);
      }
    } finally {
      // setIsLoading(false)
    }
  };

  const updateActivity = useCallback(async () => {
    try {
      const oktaUser = await oktaService.getUser();
      if (oktaUser.access_token) {
        await axios.post(`${backendApiUrl}/edit/update-activity`, {
          stage,
          component,
          projectId,
          palletId
        }, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${oktaUser.access_token}`,
            // issite: 'true'
          }
        });
      }
    } catch (error) {
      console.error('Error updating activity:', error);
    }
  }, [stage, component, projectId, palletId]);

  useEffect(() => {
    if (!isEditing) return;

    const interval = setInterval(() => void updateActivity(), 5 * 60 * 1000);
    const timeout = setTimeout(() => void releaseEditAccess(), 20 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isEditing, releaseEditAccess, updateActivity]);

  // Add beforeunload event listener when user is editing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isEditing) {
        // Show a warning when user tries to close while editing
        e.preventDefault();
        // Note: Browsers standardize this message for security reasons
        // and will show their own generic message instead
        
        return "";
      }
    };

    // Show toast when user switches tabs while editing
    const handleVisibilityChange = () => {
      if (isEditing && document.visibilityState === 'hidden') {
        // Show toast message - this will appear when they return to the tab
        showWarningToast('Please consider stop editing if you plan to leave this page for a long time');
      }
    };

    if (isEditing) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // Clean up the event listeners when component unmounts or isEditing changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isEditing]);

  return (
    <div className="bg-bgYellow border-b border-yellow-200 p-2  flex items-center gap-6">

      <div className='flex items-center gap-10 lg:gap-24'>
        <div className="flex items-center gap-4">
          {isEditing ? (
            <InfoIcon className="text-green-500" />
          ) : (
            <AlertCircle className="text-yellow-500" />
          )}
          <EditingMessage isEditing={isEditing} currentEditor={currentEditor} />

        </div>
        <Button

          className={`${isEditing ? " bg-red-500" : " bg-blue"} hover:bg-[#00336C] hover:text-white h-7 py-4 tracking-wider px-8`}
          // variant={isEditing ? "destructive":'default' }
          onClick={isEditing ? async () => {
            setIsLoading(true)
            await releaseEditAccess()
            setIsLoading(false)
          } : async () => {
            setIsLoading(true)
            await requestEditAccess()
            setIsLoading(false)
          }}
          disabled={!!currentEditor && !isEditing || isLoading}
        >
          {isEditing ? "Stop Editing" : "Enable Editing"}
          {isLoading && <span><Loader2 size={20} className="animate-spin" /></span>}
        </Button>
      </div>
    </div>
  );
};

export default EditBanner;
